import { useBetraegeSichtbar, verdeckt } from "../../../app/betraege";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading } from "../../../components/States";
import { PillView, Toolbar, useAction } from "../../../components/ui";
import { date, money as moneyFmt, monthLabel } from "../../../lib/format";
import { Ansichtswahl, KampagnenDialog, KoopLeer, StatusDialog, geklappt } from "./bausteine";
import { setzeMonatsstand } from "./daten";
import { useHeute, useKooperationen, useRechnungsAnsicht } from "./hooks";
import {
  CONTRACT_PILL, MONTH_BASED, anzeigeStand, baueZeilen, invoicePillFallback, kampagnenStand, labelMap, leistungsstand, monatStorniert, monthText, nachMarke, notizTeile, passtFilter,
  passtSuche, sortiereVertraege, statusPill, type KoopZeile,
} from "./model";
import type { InvoiceRow, StatusLabel } from "./typen";
import { t, ta } from "../../../lib/sprache";

/**
 * Kooperationen nach Marke (Hub des bisherigen Portals, AD-070 … AD-072): je Marke die Kooperationen mit Creator, Verträgen,
 * Rechnungen je Monat und internen Informationen. Erledigte sind ausgeblendet, monatliche mit offenen Monaten bleiben sichtbar.
 */
export function NachMarke() {
  const sicht = useBetraegeSichtbar();
  const money = verdeckt(sicht, moneyFmt);
  const ws = useStaffWorkspace();
  const q = useKooperationen();
  const today = useHeute();
  const [params, setParams] = useSearchParams();
  const search = params.get("q") ?? "";
  const mitErledigten = params.get("erledigt") === "1";
  const zeilen = useMemo(() => (q.data ? baueZeilen(q.data, { today, timeZone: ws.org.timezone, selbst: !!ws.isCreator }) : []), [q.data, today, ws.org.timezone]);
  const rechnung = useRechnungsAnsicht(q.data?.periods, q.data?.invoices);
  const [neueKampagne, setNeueKampagne] = useState<{ brandId: string; brandName: string; currency: string } | null>(null);

  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending || !q.data) return <Loading />;
  const sichtbar = zeilen.filter((z) => !z.archiviert);
  const erledigt = sichtbar.filter((z) => z.status.erledigt).length;
  const gruppen = nachMarke(sichtbar.filter((z) => (mitErledigten || passtFilter(z, "aktiv")) && passtSuche(z, search)));
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <main id="main" className="content tk-koop tk-nach-marke">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account")}</div>
          <h1>{ta("Kooperationen nach Marke", "Deals by brand", "Deals nach Marke", "Deals by brand")}</h1>
          <p>{t("Marke, Kooperation, Creator, Vertrag, Rechnungen und interne Informationen auf einen Blick.", "Brand, deal, creator, contract, invoices and internal information at a glance.")}</p>
        </div>
        <Ansichtswahl aktuell="marke" />
      </div>
      {sichtbar.length === 0 ? (
        <KoopLeer talents={q.data?.talents ?? []} />
      ) : (
        <>
          <Toolbar search={search} onSearch={(v) => setParam("q", v)} label={t("Marke, Kooperation oder Creator suchen", "Search brand, deal or creator")} placeholder={t("Marke, Kooperation, Creator suchen …", "Search brand, deal, creator …")}>
            {erledigt > 0 && (
              <button aria-pressed={mitErledigten} onClick={() => setParam("erledigt", mitErledigten ? "" : "1")}>
                {mitErledigten ? t("Erledigte ausblenden", "Hide completed") : t(`Erledigte anzeigen · ${erledigt}`, `Show completed · ${erledigt}`)}
              </button>
            )}
          </Toolbar>
          {gruppen.length === 0 && (
            <div className="panel stack">
              <p>{t("Keine passenden Ergebnisse. Passe die Suche oder die Filter an.", "No matching results. Adjust the search or the filters.")}</p>
              <div className="row"><button onClick={() => setParams(new URLSearchParams(), { replace: true })}>{t("Filter zurücksetzen", "Reset filters")}</button></div>
            </div>
          )}
          {gruppen.map((g) => (
            <section key={g.brandId} className="panel stack">
              <div className="row between">
                <h3>{g.name}</h3>
                <div className="row">
                  <small>{g.zeilen.length === 1 ? t("1 Kooperation", "1 deal") : t(`${g.zeilen.length} Kooperationen`, `${g.zeilen.length} deals`)}</small>
                  <button onClick={() => setNeueKampagne({ brandId: g.brandId, brandName: g.name, currency: g.zeilen[0]?.deal.currency ?? ws.org.currency })}>{t("Neue Kampagne", "New campaign")}</button>
                  <Link className="btn" to={`/agentur/marken/${g.brandId}`}>{t("Marke öffnen", "Open brand")}</Link>
                </div>
              </div>
              {q.data.campaigns.filter((c) => c.brand_id === g.brandId && c.status === "active").map((c) => {
                const k = kampagnenStand(c, zeilen);
                return (
                  <Link key={c.id} className="task" to={`/agentur/kooperationen/kampagne/${c.id}`}>
                    <div>
                      <strong>{t("Kampagne ", "Campaign ")}{c.title}</strong>
                      <p>{k.zeilen.length === 1 ? t("1 Kooperation", "1 deal") : t(`${k.zeilen.length} Kooperationen`, `${k.zeilen.length} deals`)} · {k.creator === 1 ? t("1 Creator", "1 creator") : t(`${k.creator} Creator`, `${k.creator} creators`)}{c.deadline ? t(` · Deadline ${date(c.deadline)}`, ` · deadline ${date(c.deadline)}`) : ""}{c.budget_cents != null ? t(` · ${money(k.honorarCents, c.currency)} von ${money(c.budget_cents, c.currency)} verplant`, ` · ${money(k.honorarCents, c.currency)} of ${money(c.budget_cents, c.currency)} allocated`) : ""}</p>
                    </div>
                    <span className="pill info"><span className="pill-zeichen">◇</span> {t("Kampagne", "Campaign")}</span>
                  </Link>
                );
              })}
              <div className="tablewrap tk-koop-karten">
                <table>
                  <caption className="sr-only">{t("Kooperationen mit ", "Deals with ")}{g.name}</caption>
                  <thead>
                    <tr>
                      <th scope="col">{t("Kooperation", "Deal")}</th>
                      <th scope="col">Creator</th>
                      <th scope="col">{t("Vertrag", "Contract")}</th>
                      <th scope="col">{t("Rechnungen", "Invoices")}</th>
                      <th scope="col">{t("Interne Informationen", "Internal information")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.zeilen.map((z) => <HubZeile key={z.deal.id} z={z} rechnung={rechnung} labels={q.data?.labels ?? []} />)}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
          <p className="source-note">{t("Klicke auf eine Kooperation, einen Vertrag oder eine Rechnung, um Details zu öffnen.", "Click on a deal, a contract or an invoice to open details.")}</p>
        </>
      )}
      {neueKampagne && (
        <KampagnenDialog open onClose={() => setNeueKampagne(null)} kampagne={null} neu={{ orgId: ws.org.org_id, ...neueKampagne }} />
      )}
      {api.mode === "demo" && <p className="demo-note">{t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictional.")}</p>}
    </main>
  );
}

function HubZeile({ z, rechnung, labels }: { z: KoopZeile; rechnung: ReturnType<typeof useRechnungsAnsicht>; labels: readonly StatusLabel[] }) {
  // K-541: Monatsstand direkt hier setzen (Chip je Monat, Klick öffnet den Status-Dialog als Rückfrage)
  const [monatDlg, setMonatDlg] = useState<KoopZeile["monate"][number] | null>(null);
  const { busy, run } = useAction();
  const own = labelMap([...labels]);
  const sicht = useBetraegeSichtbar();
  const money = verdeckt(sicht, moneyFmt);
  const [alleVertraege, setAlleVertraege] = useState(false);
  const [alleRechnungen, setAlleRechnungen] = useState(false);
  const deal = z.deal;
  const vertraege = sortiereVertraege(z.vertraege);
  const invoices: { row: InvoiceRow; month: string | null }[] = [
    ...z.monate.flatMap((m) => m.invoices.map((e) => ({ row: e.row, month: m.month }))),
    ...z.ohneMonat.map((e) => ({ row: e.row, month: e.row.revenue_month?.slice(0, 7) ?? null })),
  ].filter((x) => !["withdrawn", "discarded"].includes(x.row.status))
    .sort((a, b) => (b.month ?? "").localeCompare(a.month ?? "") || (b.row.issue_date ?? "").localeCompare(a.row.issue_date ?? ""));
  const shownContracts = alleVertraege ? vertraege : vertraege.slice(0, 1);
  const shownInvoices = alleRechnungen ? invoices : invoices.slice(0, 1);
  const done = z.monate.filter((m) => m.done).length;
  const brand = z.brand as (typeof z.brand & { invoice_email?: string | null }) | undefined;
  const mail = deal.billing_email ?? brand?.invoice_email ?? null;
  const hinweis = [deal.billing_reference ? t(`Referenz ${deal.billing_reference}`, `Reference ${deal.billing_reference}`) : null, deal.billing_payment_terms_days != null ? t(`Zahlungsziel ${deal.billing_payment_terms_days} Tage`, `Payment term ${deal.billing_payment_terms_days} days`) : null].filter(Boolean).join(" · ");
  const note = z.terms?.internal_note ?? "";
  return (
    <tr>
      <td className="tk-koop-zelle tk-koop-kopf">
        <Link to={`/agentur/kooperationen/${deal.id}`} title={deal.title}><strong>{deal.title}</strong></Link>
        <small><PillView pill={z.status.pill} /></small>
        {z.kampagne && <small>{t("Kampagne ", "Campaign ")}{z.kampagne.title}</small>}
        {z.status.hinweis && <small>{z.status.hinweis}</small>}
        {z.monate.length > 1 && (
          <small>
            {done} {t("von ", "of ")}{z.monate.length} {t("Monaten erledigt", "months done")}<span className="progress tk-koop-fortschritt" role="img" aria-label={t(`${done} von ${z.monate.length} Monaten erledigt`, `${done} of ${z.monate.length} months done`)}>
              <span style={{ width: `${Math.round((done / z.monate.length) * 100)}%` }} />
            </span>
          </small>
        )}
        {z.monate.length > 1 && MONTH_BASED.has(deal.model) && !deal.archived_at && (
          <span className="tk-monat-chips" role="group" aria-label={t("Monatsstand", "Month status")}>
            {z.monate.map((m) => {
              const st = anzeigeStand(deal, m);
              const p = statusPill(st, own);
              return (
                <button key={m.period.id} type="button" className={`pill ${p.tone} tk-monat-chip`} disabled={!!busy || monatStorniert(m.period)}
                  title={t(`${monthText(m.month)}: ${p.label}. Klick setzt den Monatsstand.`, `${monthText(m.month)}: ${p.label}. Click to set the month status.`)}
                  onClick={() => setMonatDlg(m)}>
                  {m.done ? "✓ " : ""}{monthLabel(m.month.slice(0, 7), false).slice(0, 3)} {m.month.slice(2, 4)} · {p.label}
                </button>
              );
            })}
          </span>
        )}
        {monatDlg && (
          <StatusDialog
            open
            onClose={() => setMonatDlg(null)}
            titel={t(`Monatsstand · ${monthText(monatDlg.month)}`, `Month status · ${monthText(monatDlg.month)}`)}
            text={t(`Die Änderung gilt nur für ${monthText(monatDlg.month)} von „${deal.title}“. Rechnungsstände zieht die Datenbank aus den Rechnungen nach.`, `The change only applies to ${monthText(monatDlg.month)} of “${deal.title}”. Invoice statuses follow from the invoices.`)}
            aktuell={leistungsstand(deal, monatDlg)}
            labels={labels}
            busy={!!busy}
            onSave={(st) => run("monat-stand", () => geklappt(setzeMonatsstand(api, { deal, periodId: monatDlg.period.id, status: st, auchKooperation: false })), t("Monatsstand gespeichert.", "Month status saved."))}
          />
        )}
      </td>
      <td data-label="Creator"><Link to={`/agentur/creator/${deal.talent_id}`}>{z.talentName}</Link></td>
      <td className="tk-koop-zelle" data-label={t("Vertrag", "Contract")}>
        {shownContracts.map((c) => (
          <small key={c.id}><Link to={`/agentur/vertraege/${c.id}`}>{c.title}</Link>{c.link === "link" ? t(" · Rahmenvertrag", " · framework contract") : ""} <PillView pill={CONTRACT_PILL[c.status]} /></small>
        ))}
        {vertraege.length === 0 && <span className="muted">{t("Kein Vertrag verknüpft.", "No contract linked.")}</span>}
        {vertraege.length > 1 && (
          <button aria-expanded={alleVertraege} onClick={() => setAlleVertraege((v) => !v)}>{alleVertraege ? t("Weniger", "Less") : t(`+${vertraege.length - 1} weitere`, `+${vertraege.length - 1} more`)}</button>
        )}
      </td>
      <td className="tk-koop-zelle" data-label={t("Rechnungen", "Invoices")}>
        {shownInvoices.map(({ row, month }) => {
          const v = rechnung(row);
          return (
            <small key={row.id}>
              <Link to={`/agentur/rechnungen/${row.id}`}>{row.number ?? t("Ohne Nummer", "Without number")}</Link>
              {month ? ` · ${monthText(month)}` : ""} · {money(row.gross_cents, row.currency)} <PillView pill={v?.pill ?? invoicePillFallback(row.status)} />
            </small>
          );
        })}
        {invoices.length === 0 && <span className="muted">{t("Keine Rechnungen.", "No invoices.")}</span>}
        {invoices.length > 1 && (
          <button aria-expanded={alleRechnungen} onClick={() => setAlleRechnungen((v) => !v)}>{alleRechnungen ? t("Weniger", "Less") : t(`+${invoices.length - 1} weitere`, `+${invoices.length - 1} more`)}</button>
        )}
      </td>
      <td className="tk-koop-zelle" data-label={t("Intern", "Internal")}>
        {mail && <small>{t("Marke: ", "Brand: ")}{mail}</small>}
        {hinweis && <small>{t("Rechnungshinweis: ", "Invoice note: ")}{hinweis}</small>}
        {note && (
          <small>
            {t("Interne Notiz: ", "Internal note: ")}{notizTeile(note).map((t, i) => (t.href ? <a key={i} href={t.href} target="_blank" rel="noreferrer noopener">{t.text}</a> : <span key={i}>{t.text}</span>))}
          </small>
        )}
        {!mail && !hinweis && !note && <span className="muted">–</span>}
      </td>
    </tr>
  );
}
